"use client";

import { Spin, Alert } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./ForgetPassword.css";

import { useDoctorEmailCode } from "../../hooks/useDoctorEmailCode";

/** Email input */
const FormInput = ({
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  error,
  name,
  autoComplete,
}) => {
  return (
    <div className="signup-form-group">
      {label && (
        <label className="signup-form-label" htmlFor={name}>
          {label}
        </label>
      )}
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={onChange}
        className={`signup-form-input${error ? " signup-password-mismatch" : ""}`}
        autoComplete={autoComplete}
      />
      {error ? <span className="fpw-error-text">{error}</span> : null}
    </div>
  );
};

export default function ForgetPassword() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");

  const {
    email,
    handleChange,
    handleSendCode,
    loading,
    error,
  } = useDoctorEmailCode();

  return (
    <div className={`signup-container signup-doctor-auth ${isRTL ? "is-rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <div className="signup-background signup-doctor">
        <section className="signup-form-wrapper">
          <div className="signup-form">
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("forgetPassword.title", "Reset Password").split(" ").map((word, idx) => 
                  word.toLowerCase() === "password" ? 
                    <span key={idx} style={{ color: "rgba(15, 66, 177, 1)" }}>{word}</span> : 
                    <span key={idx}>{word}{idx < t("forgetPassword.title", "Reset Password").split(" ").length - 1 ? " " : ""}</span>
                )}
              </h1>
              <p className="signup-form-subtitle">{t("forgetPassword.subtitle")}</p>
            </div>

            <div className="signup-form-content">
              {loading && (
                <div className="fpw-loader">
                  <Spin tip={t("forgetPassword.loading")} />
                </div>
              )}

              {error && (
                <Alert
                  className="fpw-alert"
                  type="error"
                  message={error}
                  showIcon
                />
              )}

              <FormInput
                name="email"
                label={t("forgetPassword.emailLabel")}
                type="email"
                placeholder={t("forgetPassword.emailPlaceholder")}
                value={email}
                onChange={(e) => handleChange(e.target.value)}
                autoComplete="email"
                error={error}
              />

              <div className="signup-form-group">
                <button
                  type="button"
                  className="signup-create-account-btn"
                  disabled={!email || loading}
                  onClick={handleSendCode}
                >
                  {loading ? t("forgetPassword.sending") : t("forgetPassword.sendCode")}
                </button>

                <div className="signup-login-link-wrapper">
                  <button
                    type="button"
                    className="signup-login-link"
                    onClick={() => navigate("/LoginDoctor")}
                  >
                    {t("forgetPassword.backToLogin")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="signup-hero" style={{ backgroundImage: `url(${chooseImage})` }}>
          <div className="fpw-overlay" />
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home signup-back-home-doctor signup-hero-back-btn"
            onClick={() => navigate("/")}
            aria-label={t("common.backToHome", "Back to home page")}
          >
            <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>
          <div className="signup-hero-inner">
            <h3 className="signup-hero-title">
              {t("signup.createAccountHeroTitle", "Are you signing up as a patient?")}
            </h3>
            <p className="signup-hero-sub">
              {t("signup.createAccountHeroText", "This registration is for doctors only. To book appointments and access care, please create a patient account instead.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={() => navigate("/LoginPatient")}
            >
              <span>{t("signup.continueAsPatient", "Continue as a patient")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}